import { ReactNode } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import View from "../atoms/view";
import theme from "../../theme";
import { Styles } from "../../constants/theme";
import { Card } from "../atoms/card";
import { Label, SubTitle, TextRegular } from "../atoms/text";

export type SelectableListItem = {
  id: string;
  title: string;
  meta?: Array<string | null | undefined>;
  thumbnail?: ReactNode;
};

type SelectableListProps = {
  title: string;
  items: SelectableListItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

const hasValue = (value?: string | null) =>
  value !== undefined && value !== null && String(value).trim() !== "";

export const SelectableList = ({
  title,
  items,
  selectedIndex,
  onSelect,
}: SelectableListProps) => {
  return (
    <Card>
      <SubTitle noPadding extend={{ fontSize: "14px" }}>{title}</SubTitle>
      <View extend={styles.list}>
        {items.map((item, index) => {
          const isSelected = index === selectedIndex;
          const meta = (item.meta ?? []).filter(hasValue);

          return (
            <View
              key={item.id}
              extend={[styles.row, isSelected && styles.rowSelected]}
              onClick={() => onSelect(index)}
            >
              {item.thumbnail ? (
                <View extend={styles.thumbnail}>{item.thumbnail}</View>
              ) : null}
              <View extend={styles.rowText}>
                <TextRegular strong noPadding extend={styles.rowTitle}>{item.title}</TextRegular>
                {meta.map((line) => (
                  <Label noPadding key={`${item.id}-${line}`}>
                    {line}
                  </Label>
                ))}
              </View>
              <KeyboardArrowRightIcon sx={{ color: theme.colors.grey, flexShrink: 0 }} />
            </View>
          );
        })}
      </View>
    </Card>
  );
};

const styles = {
  panel: {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: `${Styles.BorderRadius}px`,
    padding: "16px",
    minWidth: "0",
    display: "flex",
    flexDirection: "column",
    maxHeight: "75vh",
  },
  list: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minHeight: "0",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    padding: "10px",
    borderRadius: `${Styles.BorderRadius}px`,
    cursor: "pointer",
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.white,
    boxSizing: "border-box",
  },
  rowSelected: {
    backgroundColor: theme.colors.primaryLight,
    borderColor: theme.colors.primaryLight,
  },
  thumbnail: {
    width: "56px",
    height: "56px",
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: `${Styles.BorderRadius}px`,
    backgroundColor: theme.colors.lightGrey,
  },
  rowText: {
    flex: 1,
    minWidth: "0",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  rowTitle: {
    fontFamily: "Futura",
    fontSize: "14px",
    fontWeight: 600,
    color: theme.colors.darkGrey,
  },
  rowMeta: {
    fontFamily: "Futura",
    fontSize: "12px",
    fontWeight: 400,
    color: theme.colors.grey,
  },
};
